import React from 'react';
import { Product } from 'shared/types/product/Product';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import WarningIcon from 'components/icons/warning-icon';
import { PLP_PRODUCT_CLICKED } from 'helpers/constants/events';
import { Category } from 'types/entity/category';
import { useProductList } from '../../context';
import Pagination from '../pagination';
import PlpProductTileWrapper from '../plp-product-tile-wrapper';

interface Props {
  products: Product[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    limit?: number;
  };
  currentCategory: Category[];
}

const List: React.FC<Props> = ({ products, pagination, currentCategory }) => {
  const { searchQuery, onPageClick, onNextPageClick, onPrevPageClick } = useProductList();

  return (
    <div>
      {products.length > 0 ? (
        <div className="grid grid-cols-1 gap-0 !pt-0 sm:grid-cols-2 md:grid-cols-3 md:pt-32 lg:grid-cols-4">
          {products.map((product) => (
            <PlpProductTileWrapper
              key={product.productId}
              product={product}
              isSearchResult={!!searchQuery}
              disableVariants={true}
              onClick={() => {
                gtag('event', PLP_PRODUCT_CLICKED, product);
              }}
            />
          ))}
        </div>
      ) : (
        <div className="-mt-16 flex size-full flex-col gap-10 pb-36">
          <h1 className="text-36 text-cyan-950 md:text-48">
            <span className="font-semibold">Search</span> Results
          </h1>
          <div className="mt-12 flex gap-18">
            <WarningIcon />
            <div className="flex flex-col justify-center">
              <Typography as={'h2'} className="text-2xl font-bold text-gray-700 md:text-3xl">
                No results found
              </Typography>
              <div className="text-xl font-normal tracking-tight text-gray-600 md:text-2xl">
                We didn&apos;t find any results for{' '}
                <span className="font-bold text-gray-700">
                  {searchQuery ? searchQuery : currentCategory?.[0]?.name}
                </span>
              </div>
            </div>
          </div>
          <Typography className="mt-24 text-2xl font-bold text-gray-600">Suggestions</Typography>
          <ul className="ml-20 mt-6 list-disc pl-5 text-gray-500">
            <li>Spell all words correctly</li>
            <li>
              Change or{' '}
              <Link link="/products" className="text-blue-600 underline-offset-4 hover:cursor-pointer hover:underline">
                clear
              </Link>{' '}
              keywords
            </li>
          </ul>
          <Typography className="mt-24 text-2xl font-bold text-gray-600">Keep searching!</Typography>
          <ul className="ml-20 mt-6 list-disc pl-5 text-gray-500">
            <li>And we&apos;ll keep writing. Every search you do helps us figure out what you&apos;re looking for.</li>
          </ul>
        </div>
      )}
      {pagination && products.length > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageClick={onPageClick}
          onNextPageClick={onNextPageClick}
          onPrevPageClick={onPrevPageClick}
        />
      )}
    </div>
  );
};

export default List;
